import { ArrowLeftRight } from "lucide-react";
import { AGENCY_TZ, formatDate } from "../api";
import { cleanTitle } from "../task-title";
import QuickMenu, { QuickMenuItem } from "./QuickMenu";
import { label } from "./Workflow";

export const BOARD_COLUMNS = ["backlog", "todo", "in_progress", "review", "done"] as const;
export type BoardTask = {
  id: number;
  title: string;
  project_id: number | null;
  project_name: string | null;
  due_date: string | null;
  client_id: number | null;
  client_name: string | null;
  assignee_name: string | null;
};

/** "Oct 7", plus the year only when it is not this year. */
function shortDate(value: string) {
  const d = new Date(`${value.slice(0, 10)}T12:00:00`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(d.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}) });
}
/** Due before today in the agency's time zone, whoever is looking. */
function isPast(value: string | null) {
  if (!value) return false;
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: AGENCY_TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  return value.slice(0, 10) < today;
}

/**
 * Task cards in Backlog to Done, shared by the agency Projects board and a
 * client's Projects board. A card opens its task in place (onOpen) and moves
 * with the arrows menu; empty columns say so.
 */
export default function ProjectBoard({
  board,
  include,
  showClient = false,
  busy = false,
  onOpen,
  onMove,
}: {
  board: Partial<Record<string, BoardTask[]>> | null | undefined;
  /** Which tasks to show (the agency board's search and filters). */
  include?: (task: BoardTask) => boolean;
  /** The agency board names each card's client; a client's own board does not. */
  showClient?: boolean;
  busy?: boolean;
  onOpen: (task: BoardTask) => void;
  onMove: (task: BoardTask, column: string) => void;
}) {
  return (
    <div className="board-columns ap-board">
      {BOARD_COLUMNS.map((column) => {
        const tasks = (board?.[column] || []).filter((task) => !include || include(task));
        return (
          <section className="board-column" key={column}>
            <h2>{label(column)}<span className="ap-count">{tasks.length}</span></h2>
            {tasks.map((task) => {
              const title = cleanTitle(task.title);
              const late = column !== "done" && isPast(task.due_date);
              const context = showClient ? `${task.client_name || "Agency"}${task.project_name ? ` · ${task.project_name}` : ""}` : task.project_name || "";
              return (
                <article className="board-card ap-card" key={task.id}>
                  <button type="button" className="ap-card-title" onClick={() => onOpen(task)}>{title}</button>
                  {context && <span className="ap-card-context" title={context}>{context}</span>}
                  <div className="ap-card-meta">
                    <span className="ap-card-owner" title={task.assignee_name || "Unassigned"}>{task.assignee_name || "Unassigned"}</span>
                    {task.due_date && (
                      <time className={late ? "ap-card-due late" : "ap-card-due"} dateTime={task.due_date.slice(0, 10)} title={late ? `Overdue since ${formatDate(task.due_date)}` : `Due ${formatDate(task.due_date)}`}>
                        {late && <span className="sr-only">Overdue, </span>}{shortDate(task.due_date)}
                      </time>
                    )}
                    <QuickMenu label={`Move ${title}`} align="right" disabled={busy} trigger={<span className="chip chip-ghost chip-sm ap-move" title="Move to another column"><ArrowLeftRight aria-hidden="true" /></span>}>
                      {(close) => BOARD_COLUMNS.map((value) => (
                        <QuickMenuItem key={value} active={value === column} onClick={() => { close(); if (value !== column) onMove(task, value); }}>{label(value)}</QuickMenuItem>
                      ))}
                    </QuickMenu>
                  </div>
                </article>
              );
            })}
            {!tasks.length && <p className="ap-empty">Nothing here</p>}
          </section>
        );
      })}
    </div>
  );
}
